// this data is used to contruct analytics data layer
import { Inject, Injectable } from '@angular/core';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { UserAgentService } from '@rd/core/services/user-agent/user-agent.service';
import { ENV } from '@rd/core/tokens/rd-lib.token';
import { TrackData } from '@rd/types/analytics.type';
import { Sitemap } from '@rd/types/sitemap.type';
import { EnvironmentType, TodoFixItLater } from 'global-types';

export interface WestpacSitemap
  extends Pick<Sitemap, 'homepage' | 'offers' | 'giftCard'> {}

export const sitemap: WestpacSitemap = {
  homepage: 'home',
  giftCard: {
    listing: 'giftcard',
    checkout: 'giftcard:card details',
    review: 'giftcard:summary',
    success: 'giftcard:confirmation',
    manage: 'giftcard:manage',
  },
  offers: {
    listing: 'special offers:offer',
    detailsFullData: 'special offers:offer details',
  },
};

@Injectable({
  providedIn: 'root',
})
export class WestpacAnalyticsService extends AnalyticsService {
  protected siteBrand = '';
  protected analyticsUrl = '';
  protected offsiteScriptUrl = '';
  protected isEnabled = true;
  sitemap = sitemap;

  constructor(
    @Inject(ENV) protected env: EnvironmentType,
    protected userAgentService: UserAgentService,
  ) {
    super();
    // Westpac analytics require a defined pageDetails object
    // otherwise it will log error. This error happens when
    // user visit pages doesn't track any events
    // @ts-ignore
    window.pageDetails = {};
    this.userAgentService.checkUserAgent();
  }

  protected trackEvent(trackParams: TrackData): void {
    if (this.env !== 'production' && !this.siteBrand) {
      console.error('siteBrand has not been set');
    }

    // VSCode understand the override rd-lib/global.d.ts but ng-cli doesn't understand.
    // @ts-ignore
    window.pageDetails = {
      siteBrand: this.siteBrand,
      siteName: 'offers',
      siteEnv: this.env === 'production' ? 'prod' : 'test',
      siteExperience: this.userAgentService.getPlatFormType(),
      pageName: `${trackParams.routeName}`,
    };

    if (
      trackParams.routeName &&
      trackParams.routeName === sitemap.offers?.detailsFullData
    ) {
      const { brand, title } = trackParams.extra || ({} as TodoFixItLater);
      if (title && brand) {
        // @ts-ignore
        window.pageDetails.pageProperty = `${brand}:${title}`;
      }
    }

    if (
      trackParams.routeName &&
      trackParams.routeName === sitemap.giftCard?.checkout
    ) {
      const { brand } = trackParams.extra || ({} as TodoFixItLater);
      if (brand) {
        // @ts-ignore
        window.pageDetails.pageProperty = brand;
      }
    }

    // @ts-ignore
    if (window.wa) {
      // @ts-ignore
      window.wa('page', window.pageDetails);
    }
  }

  protected onAnalyticsScriptLoaded(): void {
    // @ts-ignore
    if (window._satellite) {
      // ensure that we don't enable debug mode in production
      if (this.env === 'local') {
        // enable debug mode if we need view analytics log in console
        // We can also view the log in the chrome extension
        // https://chrome.google.com/webstore/detail/adobe-experience-cloud-de/ocdmogmohccmeicdhlhhgepeaijenapj
        // @ts-ignore
        // window._satellite.setDebug(true);
      }
      // @ts-ignore
      window._satellite.pageBottom();
    }
  }

  injectAnalyticsScript(): void {
    if (this.analyticsUrl && this.offsiteScriptUrl) {
      this.initScript(window, document, 'script', this.offsiteScriptUrl, 'wa');
      const sharedScript = document.createElement('script');
      sharedScript.src = this.analyticsUrl;
      document.head.appendChild(sharedScript);
    }
  }

  // why do we use any here?
  // this function is copy from the snipper provided by Westpac
  // we don't need to waste time to make it typesafe with no benefit
  // check Westpac analytics document for the original snippet
  // https://kaligo.atlassian.net/wiki/spaces/PROD/pages/1414103164/Westpac+Web+Analytics+Specifications
  initScript(
    windowObj: any,
    documentObj: any,
    tagName: any,
    analyticScriptUrl: any,
    analyticsVariableName: any,
  ): void {
    let scriptTag: HTMLScriptElement;
    let d: any;
    function track(): void {
      (windowObj[analyticsVariableName].q =
        windowObj[analyticsVariableName].q || []).push(arguments);
    }

    windowObj.WBGAnalyticsObject = analyticsVariableName;
    windowObj[analyticsVariableName] =
      windowObj[analyticsVariableName] || track;
    windowObj[analyticsVariableName].config = {};
    windowObj[analyticsVariableName].config.ls = +new Date();
    scriptTag = documentObj.createElement(tagName);
    d = documentObj.getElementsByTagName(tagName)[0];
    scriptTag.async = true;
    windowObj.setTimeout(() => {
      scriptTag.src = analyticScriptUrl;
      d.parentNode.insertBefore(scriptTag, d);
    }, 25);
    // this line is not available in the original snippet
    scriptTag.onload = () => {
      this.onAnalyticsScriptLoaded();
    };
  }
}
